import { computed, inject } from '@angular/core';
import { type GetObjectsFilter } from '@bizzdesign/sdk-knowledgegraph';
import { assertIsUUID, type UUID } from '@bizzdesign/bizzdesign-sdk/uuid';
import {
  AttributeCategoryDefinitionsService,
  ContentDetailsGlobalStore,
} from '@bizzdesign/data-access-content-details';
import { ElementCollectionService } from '@bizzdesign/data-access-element-collections';
import {
  type ChartConfiguration,
  type ChartFunction,
  type ChartInputs,
  type ElementCollection,
  type HighchartsContent,
  type ToolContentUpdate,
  type ObjectDto,
  type AuthenticatedUser,
  type AttributeCategoryDefinitionDto,
  type ElementSource,
} from '@bizzdesign/shared/models';
import { LayoutContextService } from '@bizzdesign/shared/services';
import {
  VISUALIZATION_TEMPLATE_REGISTER,
  type VisualizationTemplateRegister,
} from '@bizzdesign/shared/utils';
import * as designTokens from '@bizzdesign/styles';
import { TranslocoService } from '@jsverse/transloco';
import { tapResponse } from '@ngrx/operators';
import {
  patchState,
  signalStore,
  withComputed,
  withHooks,
  withMethods,
  withState,
} from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import type * as Highcharts from 'highcharts';
import {
  catchError,
  combineLatestWith,
  defer,
  EMPTY,
  filter,
  map,
  of,
  pipe,
  switchMap,
  take,
  tap,
  throwError,
  type Subscription,
} from 'rxjs';
import { mapObjectDtoToContentItem } from '../../mappers/mapper.utils';
import {
  addGeneralChartConfigurations,
  addPreviewChartConfigurations,
} from '@bizzdesign/shared/visualization-templates';
import {
  getFilterItemsFromAdvancedFilter,
  getScopeFilterOptions,
  type FilterOption,
} from '@bizzdesign/data-access-search';
import { SessionService } from '@bizzdesign/auth';
import { type SearchScope } from '@bizzdesign/sdk-search';

interface LoadInput {
  dataset: UUID[];
  configuration: ChartConfiguration;
  needsPreview: boolean;
  previewMode?: boolean;
  sourceQuery?: ElementSource;
}

interface RefreshInput {
  sourceQuery?: ElementSource;
}

interface StoreState {
  dataset: UUID[];
  configuration: ChartConfiguration | undefined;
  needsPreview: boolean;
  previewMode: boolean;
  loadingState: 'none' | 'loading' | 'loaded' | 'changed' | 'error';
  objectsList: ObjectDto[];
  chartOptions: Highcharts.Options | undefined;
  previewCallback: (() => string) | undefined;
  showChart: boolean;
  sourceQuery: ElementSource | undefined;
  _collection: ElementCollection | undefined;
  _authenticatedUser?: AuthenticatedUser | undefined;
  _scopeFilterOptions: FilterOption<SearchScope>[];
  _attributeCategoryDefinitions?: AttributeCategoryDefinitionDto[];
}

const initialState: StoreState = {
  dataset: [],
  configuration: undefined,
  needsPreview: false,
  previewMode: false,
  loadingState: 'none',
  objectsList: [],
  chartOptions: undefined,
  previewCallback: undefined,
  showChart: false,
  sourceQuery: undefined,
  _collection: undefined,
  _authenticatedUser: undefined,
  _scopeFilterOptions: [],
  _attributeCategoryDefinitions: [],
};

export const HighchartsLocalStore = signalStore(
  withState(initialState),
  withComputed(
    ({
      loadingState,
      sourceQuery,
      _authenticatedUser,
      _scopeFilterOptions,
      _attributeCategoryDefinitions,
    }) => ({
      showLoader: computed(
        () => loadingState() === 'none' || loadingState() === 'loading',
      ),
      appliedFilters: computed(() => {
        const advancedFilter = sourceQuery()?.advancedFilter;
        return advancedFilter
          ? getFilterItemsFromAdvancedFilter(
              advancedFilter,
              _scopeFilterOptions(),
              _authenticatedUser(),
              _attributeCategoryDefinitions(),
            )
          : [];
      }),
    }),
  ),
  withMethods(
    (
      store,
      translocoService = inject(TranslocoService),
      elementCollectionService = inject(ElementCollectionService),
      contentDetailsGlobalStore = inject(ContentDetailsGlobalStore),
      layoutContextService = inject(LayoutContextService),
      visualizationTemplateRegister = inject<VisualizationTemplateRegister>(
        VISUALIZATION_TEMPLATE_REGISTER,
      ),
    ) => {
      function beforeLoadCollection(
        dataset: UUID[],
        configuration: ChartConfiguration,
        needsPreview?: boolean,
        sourceQuery?: ElementSource,
        previewMode?: boolean,
      ) {
        patchState(store, {
          dataset,
          configuration,
          needsPreview: !!needsPreview,
          previewMode: !!previewMode,
          sourceQuery,
          loadingState: 'loading',
          showChart: false,
          chartOptions: undefined,
        });
      }

      function loadCollection(
        dataset: UUID[],
        filterSpec?: GetObjectsFilter<'BDCore'>,
      ) {
        return elementCollectionService.getCollection(dataset, filterSpec);
      }

      const handleElementClick = rxMethod<{ objectId: UUID }>(
        pipe(
          switchMap(({ objectId }) =>
            layoutContextService.context$.pipe(
              take(1),
              map((ctx) => ({ ctx, objectId })),
            ),
          ),
          filter(({ ctx }) => ctx === 'primary'),
          map(({ objectId }) => {
            const collection = store._collection();
            const obj =
              collection?.objects?.find(
                (o: { id: UUID }) => o.id === objectId,
              ) ?? undefined;
            return obj;
          }),
          tap((object) => {
            if (object) {
              contentDetailsGlobalStore.updateContentItem(
                mapObjectDtoToContentItem(object),
              );
              contentDetailsGlobalStore.updateIsDrawerOpen(true);
            } else {
              console.warn('Object not found in current collection');
            }
          }),
          catchError((err) => {
            console.error('Failed to handle element click (lookup)', err);
            return EMPTY;
          }),
        ),
      );

      function chartOptions$(
        configuration: ChartConfiguration,
        collection: ElementCollection,
        previewMode?: boolean,
      ) {
        const template =
          visualizationTemplateRegister[configuration.visualizationId];
        if (!template?.chartFunction) {
          return throwError(
            () =>
              new Error(
                `Visualization template '${configuration.visualizationId}' not registered`,
              ),
          );
        }
        const chartFn: ChartFunction = template.chartFunction;

        const onClickElementCallbackFn = (objectId: string) => {
          assertIsUUID(objectId);
          handleElementClick({ objectId });
        };

        return translocoService
          .selectTranslateObject(
            `visualizations.${configuration.visualizationId}`,
            {},
            'featureHighcharts',
          )
          .pipe(
            combineLatestWith(
              translocoService.selectTranslateObject(
                `filter`,
                {},
                'featureHighcharts',
              ),
            ),
            take(1),
            map(([chartTranslations, filterTranslation]) => {
              const translations = {
                ...chartTranslations,
                ...filterTranslation,
              };
              const chartInputs: ChartInputs = {
                translations,
                designTokens: extractStringDesignTokens(designTokens),
                onClickElementCallbackFn,
                chartModifier: configuration.chartModifier,
              };

              const options: Highcharts.Options = chartFn(
                collection,
                chartInputs,
              ).chartOptions;

              return previewMode
                ? addPreviewChartConfigurations(options)
                : addGeneralChartConfigurations(options);
            }),
          );
      }

      const loadChartOptions = rxMethod<{ input: LoadInput }>(
        pipe(
          tap(({ input }) =>
            beforeLoadCollection(
              input.dataset,
              input.configuration,
              input.needsPreview,
              input.sourceQuery,
              input.previewMode,
            ),
          ),
          switchMap(({ input }) =>
            defer(() =>
              loadCollection(
                input.dataset,
                input.configuration.chartModifier?.filterSpec,
              ),
            ).pipe(
              switchMap((collection) =>
                chartOptions$(
                  input.configuration,
                  collection,
                  input.previewMode,
                ).pipe(map((chartOptions) => ({ chartOptions, collection }))),
              ),
              tapResponse({
                next: ({ chartOptions, collection }) => {
                  patchState(store, {
                    loadingState: 'loaded',
                    chartOptions,
                    objectsList: collection.objects,
                    _collection: collection,
                  });
                  queueMicrotask(() => patchState(store, { showChart: true }));
                },
                error: () =>
                  patchState(store, {
                    loadingState: 'error',
                    showChart: false,
                  }),
              }),
            ),
          ),
        ),
      );

      const refreshChartOptions = rxMethod<void>(
        pipe(
          switchMap(() => {
            const sourceQuery = store.sourceQuery();
            const configuration = store.configuration();
            if (configuration === undefined) {
              return EMPTY;
            }
            // Keep showChart and chartOptions intact during refresh so the existing
            // Highcharts chart instance stays mounted. Destroying and recreating the
            // chart resets the Pointer, but container.onclick is not cleared by
            // Pointer.destroy(); a queued click that fires in the gap between
            // destruction and recreation reads this.chart as undefined and crashes
            // with "Cannot read properties of undefined (reading 'hoverPoint')".
            patchState(store, {
              loadingState: 'loading',
            });
            const dataset$ = sourceQuery
              ? sourceQuery.objects$.pipe(
                  take(1),
                  map((objects) =>
                    objects
                      .filter(
                        (o) =>
                          sourceQuery.selectedIds === undefined ||
                          sourceQuery.selectedIds.includes(o.id),
                      )
                      .map((o) => o.id),
                  ),
                )
              : of(store.dataset());
            return dataset$.pipe(
              map((dataset) => ({
                dataset,
                configuration,
                needsPreview: store.needsPreview(),
                sourceQuery,
              })),
              tap((input) => patchState(store, { dataset: input.dataset })),
              switchMap((input) =>
                loadCollection(
                  input.dataset,
                  input.configuration.chartModifier?.filterSpec,
                ).pipe(
                  switchMap((collection) =>
                    chartOptions$(
                      input.configuration,
                      collection,
                      store.previewMode(),
                    ).pipe(
                      map((chartOptions) => ({ chartOptions, collection })),
                    ),
                  ),
                ),
              ),
              tapResponse({
                next: ({ chartOptions, collection }) => {
                  patchState(store, {
                    loadingState: 'changed',
                    chartOptions,
                    objectsList: collection.objects,
                    _collection: collection,
                  });
                  // No-op on a normal refresh (showChart never goes false); deferred so a
                  // remount after a failed refresh happens once chartOptions is bound.
                  // switchMap cancels the stream but not a queued callback, so re-check
                  // that this refresh is still the current one before showing the chart.
                  queueMicrotask(() => {
                    if (store.loadingState() === 'changed') {
                      patchState(store, { showChart: true });
                    }
                  });
                },
                error: () =>
                  patchState(store, {
                    loadingState: 'error',
                    showChart: false,
                  }),
              }),
            );
          }),
        ),
      );

      function getContentUpdate(): ToolContentUpdate<HighchartsContent> {
        const preview: string | undefined = store.previewCallback()?.();
        const dataset = store.dataset();
        const configuration = store.configuration();
        if (!configuration) {
          return preview ? { preview } : {};
        }
        const toolContent: HighchartsContent = {
          dataset,
          configuration,
          needsPreview: !preview && store.needsPreview(),
        };
        return { ...(preview ? { preview } : {}), content: toolContent };
      }

      function setPreviewCallback(previewCallback: () => string): void {
        patchState(store, { previewCallback });
      }

      return {
        loadChartOptions,
        refreshChartOptions,
        handleElementClick,
        getContentUpdate,
        setPreviewCallback,
      };
    },
  ),
  withHooks((store) => {
    const sessionService = inject(SessionService);
    const attributeCategoryDefinitionsService = inject(
      AttributeCategoryDefinitionsService,
    );
    let getAuthenticatedUserSub: Subscription | null = null;
    let getAttributeCategoryDefinitionsSub: Subscription | null = null;
    return {
      onInit() {
        patchState(store, {
          _scopeFilterOptions: getScopeFilterOptions(),
        });
        getAuthenticatedUserSub = sessionService
          .getAuthenticatedUser()
          .subscribe((user) => {
            patchState(store, { _authenticatedUser: user });
          });
        getAttributeCategoryDefinitionsSub = attributeCategoryDefinitionsService
          .getAttributeCategoryDefinitions()
          .subscribe((defs) => {
            patchState(store, { _attributeCategoryDefinitions: defs });
          });
      },
      onDestroy() {
        if (getAuthenticatedUserSub) {
          getAuthenticatedUserSub.unsubscribe();
        }
        if (getAttributeCategoryDefinitionsSub) {
          getAttributeCategoryDefinitionsSub.unsubscribe();
        }
      },
    };
  }),
);

function extractStringDesignTokens(
  designTokens: Record<string, unknown>,
): Record<string, string> {
  // Charts read scalars only, and String() throws on the null-prototype
  // `variablesV2` namespace `@bizzdesign/styles` re-exports, under native ESM.
  return Object.fromEntries(
    Object.entries(designTokens)
      .filter(
        (entry): entry is [string, string | number] =>
          typeof entry[1] === 'string' || typeof entry[1] === 'number',
      )
      .map(([key, value]) => [key, String(value)]),
  );
}

export type HighchartsLocalStore = InstanceType<typeof HighchartsLocalStore>;
